import { createFileRoute, Link } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { DnsRecordPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/dns-monitoring')({
  component: DnsMonitoringPage,
  head: () => ({
    meta: [
      { title: 'DNS Monitoring — Record Change Alerts | Uptrack' },
      { name: 'description', content: 'Monitor DNS records for unexpected changes. Track A, AAAA, CNAME, MX, TXT, and NS records. Get alerted on misconfigurations, hijacks, or propagation issues.' },
      { property: 'og:title', content: 'DNS Monitoring — Record Change Alerts | Uptrack' },
      { property: 'og:description', content: 'Monitor A, AAAA, CNAME, MX, TXT, NS records. Detect DNS changes and misconfigurations instantly.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-dns-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/features/dns-monitoring' },
    ],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'DNS Monitoring', url: 'https://uptrack.app/features/dns-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack DNS Monitoring',
        description: 'DNS record monitoring with change detection for A, AAAA, CNAME, MX, TXT, and NS records.',
        url: 'https://uptrack.app/features/dns-monitoring',
      })),
    ],
  }),
})

function DnsMonitoringPage() {
  return (
    <FeaturePage
      section='DNS record monitoring'
      title='DNS monitoring that catches misconfigurations.'
      intro='Monitor A, AAAA, CNAME, MX, TXT, and NS records. Detect unauthorized changes, propagation failures, and hijack attempts before they affect your users.'
      featuresHeading='Full DNS record coverage'
      features={[
    { title: '6 record types', desc: 'Monitor A, AAAA, CNAME, MX, TXT, and NS records. Cover everything from mail routing to SPF/DKIM.' },
    { title: 'Misconfiguration detection', desc: 'Get alerted when DNS records change unexpectedly — catch provider mistakes, expired domains, or hijacks.' },
    { title: 'Custom DNS server', desc: 'Query a specific nameserver instead of relying on public resolvers. Verify propagation to your provider.' },
    { title: 'Change history', desc: 'See what changed and when. Full audit trail of every DNS record value over time.' },
      ]}
      table={{
        heading: 'Uptrack vs the alternatives',
        intro: 'Most monitoring tools either skip DNS entirely or only cover basic A records.',
        columns: ['Provider', 'DNS Monitor', 'Record Types', 'Custom Server'],
        rows: [
      { cells: ['UptimeRobot', 'No', '—', 'No'] },
      { cells: ['Better Stack', 'Yes', 'Limited', 'No'] },
      { cells: ['StatusCake', 'Paid', 'A, MX', 'No'] },
      { cells: ['Uptrack', 'All paid plans', 'A/AAAA/CNAME/MX/TXT/NS', 'Yes'], highlight: true },
        ],
      }}
      why={{ heading: 'Why DNS monitoring matters', body: 'DNS is the foundation of every web request. A wrong A record silently routes traffic to the wrong server. A missing MX record drops email into the void. DNS changes are invisible to HTTP monitors — you need dedicated DNS checks to catch them.' }}
      cta={{ heading: 'Monitor your DNS records now', body: 'Every paid plan includes 30-second checks and alert confirmation.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors">
          <DnsRecordPiece />
        </ProductFrame>
      }
    >
      <section className="container mx-auto px-4 pb-10">
        <p className="max-w-2xl text-body">
          New to DNS monitoring? Read the{' '}
          <Link to="/blog/dns-monitoring-guide" className="underline">
            DNS monitoring guide
          </Link>{' '}
          for what to check, and what an uptime tool can and cannot see.
        </p>
      </section>
    </FeaturePage>
  )
}
